import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/pro-duotone-svg-icons';
import { Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { ActiveDataRoomUser, DataRoomUser } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface UploadNdaFormProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null;
	close: () => void;
}

export const UploadNdaForm: React.FC<UploadNdaFormProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();

	const ndaMutation = useMutation(
		async ({ values }: NdaFormMutationVariables) => {
			let activeUser: ActiveDataRoomUser | null = null;
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<ActiveDataRoomUser>(
					api.post(CerebroApiEndpoints.DataRoomUsers.Nda(dataRoomGuid, user?.guid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				activeUser = data;
			}

			return activeUser;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Lender NDA was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				{user?.firstName} {user?.lastName} ({user?.bankName})
			</CerebroHeading>
			<Formik
				initialValues={{
					uploads: [] as File[],
				}}
				onSubmit={FormikUtils.MutateOnSubmit(ndaMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel>Select NDA File</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										sx={{ marginY: 3, width: '100%' }}
										onlyPdf={true}
										multiple={false}
									/>
								</Grid>
								{user?.customNdaFileName && (
									<Grid item xs={8}>
										<List dense disablePadding>
											<ListItem dense disableGutters>
												<ListItemButton
													component="a"
													target="_blank"
													rel="noreferrer"
													href={CerebroApiEndpoints.DataRoomUsers.NdaDownload(
														dataRoomGuid,
														user.guid
													)}
												>
													<ListItemIcon
														sx={theme => ({
															color: theme.palette.primary.main,
															minWidth: 30,
														})}
													>
														<FontAwesomeIcon icon={faFile} />
													</ListItemIcon>
													<ListItemText>View Current NDA</ListItemText>
												</ListItemButton>
											</ListItem>
										</List>
									</Grid>
								)}
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type NdaFormMutationVariables = FormikUtils.MutateOnSubmitVariables<NdaFormValues>;
interface NdaFormValues {
	uploads: File[];
}
type NdaFormValueErrors = FormikErrors<NdaFormValues>;
